/**
 * The order desk — every customer's orders and quotations.
 *
 * The list leads with what needs a decision. Two kinds of row are *waiting
 * for somebody here*: an order held for a credit overrun, and a quotation
 * nobody has priced. Both are drawn as warnings, so an ADV opening this page
 * can see without reading whether anything is stuck — and the kind filter
 * narrows to quotations in one click, which is how the pricing round gets
 * done.
 *
 * The search covers the customer's own reference as well as ours, because a
 * customer phoning about an order quotes the number they gave it.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import { useDebounced, useResource } from "@hardauto/core-data";
import { day, money } from "@hardauto/core-i18n";
import {
  Badge,
  Button,
  Checkbox,
  Chips,
  DataTable,
  Input,
  Page,
  Select,
  Toolbar,
  type Column,
} from "@hardauto/core-ui";

import { OrdersApi, type Order, type OrderKind, type OrderStatus } from "../api";
import { NAMESPACE } from "../i18n";
import { ORDERS_PATHS } from "../paths";
import { KINDS, STATUS_TONE, STATUSES } from "../status";
import "./orders.css";

const PAGE = 50;

export interface OrdersScreenProps {
  client: ApiClient;
  /**
   * The one kind this list shows — the back office's menu has separate
   * entries for orders and quotations. Absent, the kind is a filter.
   */
  fixedKind?: OrderKind | undefined;
  /**
   * The customer the session's context is on, when there is one. The list
   * narrows to them and says so; the context panel is where it is undone.
   */
  scopeClient?: { id: string; label: string } | null | undefined;
}

export function OrdersScreen({ client, fixedKind, scopeClient }: OrdersScreenProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new OrdersApi(client), [client]);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<OrderStatus | "">("");
  const [chosenKind, setKind] = useState<OrderKind | "">("");
  const kind = fixedKind ?? chosenKind;
  const clientId = scopeClient?.id;
  const [openOnly, setOpenOnly] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const term = useDebounced(search, 300);

  const fetcher = useCallback(
    () =>
      api.list({
        clientId: clientId || undefined,
        search: term || undefined,
        status: status || undefined,
        kind: kind || undefined,
        openOnly: openOnly || undefined,
        afterId: cursor,
        limit: PAGE,
      }),
    [api, clientId, term, status, kind, openOnly, cursor],
  );
  const orders = useResource(fetcher);

  const columns: Column<Order>[] = [
    {
      key: "reference",
      header: t("list.column.reference"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <Link to={ORDERS_PATHS.order.replace("$id", row.id)}>
            <code>{row.reference}</code>
          </Link>
          {row.delivery.customer_reference ? (
            <span className="hardauto-cell__muted">{row.delivery.customer_reference}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "date",
      header: t("list.column.date"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <span>{day(row.placed_at, locale)}</span>
          {/* A quotation's deadline, where the reader is already looking for
              a date. Without it the desk cannot tell a live quotation from
              one that lapsed a month ago. */}
          {row.valid_until ? (
            <span className="hardauto-cell__muted">
              {t("list.validUntil", { date: day(row.valid_until, locale) })}
            </span>
          ) : null}
        </div>
      ),
    },
    { key: "site", header: t("list.column.site"), render: (row) => row.delivery.site_code },
    {
      key: "total",
      header: t("list.column.total"),
      render: (row) => <span className="hardauto-num">{money(row.total, locale)}</span>,
    },
    {
      key: "status",
      header: t("list.column.status"),
      render: (row) => (
        <Badge tone={STATUS_TONE[row.status]}>
          {t(`status.${row.status}`, { defaultValue: row.status })}
        </Badge>
      ),
    },
  ];

  const filtering = Boolean(term || status || chosenKind || openOnly || clientId);

  return (
    <Page
      title={fixedKind ? t(`list.titleFor.${fixedKind}`) : t("list.title")}
      description={t("list.description")}
      eyebrow={orders.data ? t("list.count", { count: orders.data.total }) : undefined}
    >
      <Toolbar>
        <Input
          type="search"
          className="hardauto-search"
          aria-label={t("list.searchLabel")}
          placeholder={t("list.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setCursor(undefined);
          }}
        />
        <label className="hardauto-toolbar__field">
          <span className="hardauto-visually-hidden">{t("list.statusFilter")}</span>
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as OrderStatus | "");
              setCursor(undefined);
            }}
          >
            <option value="">{t("list.allStatuses")}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`status.${value}`)}
              </option>
            ))}
          </Select>
        </label>
        {fixedKind ? null : (
          <Chips
            label={t("list.kindFilter")}
            allLabel={t("list.kind.all")}
            active={kind || null}
            chips={KINDS.map((value) => ({ id: value, label: t(`list.kind.${value}`) }))}
            onSelect={(id) => {
              setKind((id ?? "") as OrderKind | "");
              setCursor(undefined);
            }}
          />
        )}
        <Checkbox
          label={t("list.openOnly")}
          checked={openOnly}
          onChange={(event) => {
            setOpenOnly(event.target.checked);
            setCursor(undefined);
          }}
        />
      </Toolbar>

      {scopeClient ? (
        <p className="hardauto-cell__muted">{t("list.scopedTo", { client: scopeClient.label })}</p>
      ) : null}

      <DataTable<Order>
        caption={t("list.caption")}
        columns={columns}
        rows={orders.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={orders.loading}
        loadingLabel={t("list.loading")}
        error={
          orders.failed
            ? { title: t("error.title"), onRetry: orders.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={
          filtering
            ? { title: t("list.filtered.title"), description: t("list.filtered.body") }
            : { title: t("list.empty.title"), description: t("list.empty.body") }
        }
      />

      <div className="hardauto-pager">
        <Button
          disabled={!orders.data?.next_cursor}
          onClick={() => setCursor(orders.data?.next_cursor ?? undefined)}
        >
          {t("list.next")}
        </Button>
      </div>
    </Page>
  );
}
